/* Atlas — the strategy marketplace.
 *
 * The whole screen is built around one uncomfortable fact: a marketplace tile
 * showing a return figure is the most misreadable object in this product. A
 * number with a big font and no context reads as a promise, and a trader who
 * reads it that way is going to lose money in a way the platform helped them
 * to.
 *
 * So the tile is designed against that:
 *
 *   * Every figure is stamped with its RESULT CLASS and its PERIOD. The API
 *     will not publish a listing whose metrics cannot state both, so there is
 *     no case where this component has to render a bare number — and it never
 *     renders one it was not given.
 *   * SIMULATED is said in words on the tile, not only in a footnote.
 *   * The tier is on the tile too: OPEN publishes its rules and shows them,
 *     EDGE seals them and says so rather than leaving a blank.
 *   * Subscribing shows the disclosure and takes an explicit acceptance, and
 *     the confirmation says plainly that nothing is running yet.
 *
 * Deploying is deliberately NOT done here. It happens on the Forge screen
 * through the ordinary deploy path, with its approval and risk checks, and
 * this screen links the resulting run back to the subscription afterwards.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useStore } from '@/store'
import { Panel } from '@/components/Panel'
import { Empty } from '@/components/viz'
import { fmtDateTime, fmtMoney } from '@/lib/format'
import { ATLAS_TIERS, PRODUCTS } from '@/lib/products'
import type { AtlasListing, AtlasSubscription } from '@/types'

type Tab = 'browse' | 'mine'

const RISK_TONE: Record<string, string> = {
  CONSERVATIVE: 'var(--ok)',
  BALANCED: 'var(--accent)',
  AGGRESSIVE: 'var(--warn)',
}

/** How a published number must be introduced, every time it is shown. */
function resultClassLabel(cls: string | undefined): string {
  switch (cls) {
    case 'BACKTEST': return 'SIMULATED — historical backtest'
    case 'PAPER': return 'PAPER — simulated execution, live prices'
    case 'LIVE': return 'LIVE — real orders'
    default: return 'UNCLASSIFIED'
  }
}

export function Atlas() {
  const can = useStore((s) => s.can)
  const canSubscribe = can('marketplace:subscribe')

  const [tab, setTab] = useState<Tab>('browse')
  const [listings, setListings] = useState<AtlasListing[]>([])
  const [subs, setSubs] = useState<AtlasSubscription[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [detail, setDetail] = useState<AtlasListing | null>(null)

  const [tier, setTier] = useState<'' | 'OPEN' | 'EDGE'>('')
  const [grade, setGrade] = useState('')
  const [query, setQuery] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      if (tier) params.tier = tier
      if (grade) params.risk_grade = grade
      if (query.trim()) params.q = query.trim()
      const [l, s] = await Promise.all([
        api.atlasListings(params),
        api.atlasSubscriptions(true),
      ])
      setListings(l)
      setSubs(s)
      setFailed(null)
    } catch (e) {
      setFailed(e instanceof ApiError && e.status === 403
        ? 'Your role does not permit browsing the marketplace.'
        : 'The marketplace could not be reached.')
    } finally {
      setLoading(false)
    }
  }, [tier, grade, query])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (!openId) { setDetail(null); return }
    let cancelled = false
    void api.atlasListing(openId)
      .then((d) => { if (!cancelled) setDetail(d) })
      .catch(() => { if (!cancelled) setDetail(null) })
    return () => { cancelled = true }
  }, [openId])

  const activeSubs = useMemo(
    () => subs.filter((s) => s.status === 'ACTIVE'), [subs])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <Header
        tab={tab} onTab={setTab}
        activeCount={activeSubs.length}
        total={listings.length}
      />

      {tab === 'browse' && (
        <>
          <Filters
            tier={tier} onTier={setTier}
            grade={grade} onGrade={setGrade}
            query={query} onQuery={setQuery}
          />
          <Panel title={`${PRODUCTS.marketplace.name} catalogue`}>
            {loading && <Empty>Loading the catalogue…</Empty>}
            {!loading && failed && <Empty>{failed}</Empty>}
            {!loading && !failed && listings.length === 0 && (
              <Empty>
                No published listings match. The desk publishes a strategy only
                after a completed backtest on the exact version being listed,
                so an empty catalogue means nothing has cleared that bar yet.
              </Empty>
            )}
            {!loading && !failed && listings.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))',
                gap: 'var(--sp-3)',
              }}>
                {listings.map((l) => (
                  <ListingCard key={l.id} listing={l}
                               onOpen={() => setOpenId(l.id)} />
                ))}
              </div>
            )}
          </Panel>
        </>
      )}

      {tab === 'mine' && (
        <MySubscriptions
          subs={subs}
          onChanged={load}
          canSubscribe={canSubscribe}
        />
      )}

      {detail && (
        <ListingDetail
          listing={detail}
          canSubscribe={canSubscribe}
          onClose={() => setOpenId(null)}
          onSubscribed={() => { setOpenId(null); void load() }}
        />
      )}
    </div>
  )
}

// ===========================================================================
function Header({ tab, onTab, activeCount, total }: {
  tab: Tab; onTab: (t: Tab) => void; activeCount: number; total: number
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      gap: 'var(--sp-4)', flexWrap: 'wrap',
    }}>
      {/* The shell carries the page title; this row holds only the tabs. */}
      <span />
      <div role="tablist" aria-label="Atlas sections" style={{
        display: 'flex', gap: 'var(--sp-1)', background: 'var(--bg-panel)',
        border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
        padding: 3,
      }}>
        {([['browse', `Catalogue (${total})`],
           ['mine', `My subscriptions (${activeCount})`]] as const).map(([id, label]) => (
          <button
            key={id} role="tab" aria-selected={tab === id}
            onClick={() => onTab(id as Tab)}
            style={{
              padding: '6px var(--sp-4)', border: 'none', cursor: 'pointer',
              borderRadius: 'var(--radius-sm)', fontFamily: 'inherit',
              background: tab === id ? 'var(--accent)' : 'transparent',
              color: tab === id ? 'var(--text-inverse)' : 'var(--text-secondary)',
              fontSize: 'var(--fs-xs)', fontWeight: 700,
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}

function Filters({ tier, onTier, grade, onGrade, query, onQuery }: {
  tier: string; onTier: (v: '' | 'OPEN' | 'EDGE') => void
  grade: string; onGrade: (v: string) => void
  query: string; onQuery: (v: string) => void
}) {
  return (
    <div style={{
      display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap',
      background: 'var(--bg-panel)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: 'var(--sp-3)',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <input
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        placeholder="Search title, summary or symbol"
        aria-label="Search listings"
        style={{ ...inputStyle, minWidth: 260, flex: 1 }}
      />
      <select value={tier} onChange={(e) => onTier(e.target.value as '' | 'OPEN' | 'EDGE')}
              aria-label="Tier" style={inputStyle}>
        <option value="">All tiers</option>
        <option value="OPEN">{ATLAS_TIERS.OPEN.label} — whitebox</option>
        <option value="EDGE">{ATLAS_TIERS.EDGE.label} — sealed</option>
      </select>
      <select value={grade} onChange={(e) => onGrade(e.target.value)}
              aria-label="Risk grade" style={inputStyle}>
        <option value="">All risk grades</option>
        <option value="CONSERVATIVE">Conservative</option>
        <option value="BALANCED">Balanced</option>
        <option value="AGGRESSIVE">Aggressive</option>
      </select>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  background: 'var(--bg-input)', color: 'var(--text-primary)',
  border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)',
  padding: '7px var(--sp-3)', fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
}

// ===========================================================================
function ListingCard({ listing, onOpen }: {
  listing: AtlasListing; onOpen: () => void
}) {
  const m = listing.published_metrics
  return (
    <article
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen() }}
      role="button" tabIndex={0}
      style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)',
        background: 'var(--bg-panel)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', padding: 'var(--sp-4)',
        boxShadow: 'var(--shadow-sm)', cursor: 'pointer', minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Tag text={ATLAS_TIERS[listing.tier].short}
             fg={listing.tier === 'OPEN' ? 'var(--accent)' : 'var(--text-primary)'} />
        <Tag text={listing.risk_grade}
             fg={RISK_TONE[listing.risk_grade] ?? 'var(--text-muted)'} />
        {listing.status !== 'PUBLISHED' && (
          <Tag text={listing.status} fg="var(--warn)" />
        )}
        {listing.subscribed && <Tag text="SUBSCRIBED" fg="var(--ok)" />}
      </div>

      <h3 style={{
        fontSize: 'var(--fs-lg)', fontWeight: 800, letterSpacing: '-0.01em',
        lineHeight: 1.25,
      }}>
        {listing.title}
      </h3>
      <p style={{
        fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
        lineHeight: 1.5, margin: 0,
      }}>
        {listing.summary}
      </p>

      {/* Every figure below is introduced by what KIND of result it is and the
          window it covers. The class line is not a footnote — it is the first
          thing above the numbers. */}
      <div style={{
        marginTop: 'auto', paddingTop: 'var(--sp-3)',
        borderTop: '1px solid var(--border)',
      }}>
        <div style={{
          fontSize: 'var(--fs-2xs)', fontWeight: 800, letterSpacing: '0.05em',
          color: m.result_class === 'LIVE' ? 'var(--ok)' : 'var(--warn)',
          textTransform: 'uppercase', marginBottom: 6,
        }}>
          {resultClassLabel(m.result_class)}
          {m.period_start && m.period_end && (
            <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
              {' · '}{m.period_start} → {m.period_end}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-5)', flexWrap: 'wrap' }}>
          <Stat label="Return" value={m.total_return} suffix="%" />
          <Stat label="Max DD" value={m.max_drawdown} suffix="%" tone="loss" />
          <Stat label="Sharpe" value={m.sharpe_ratio} />
          <Stat label="Trades" value={m.trades_count?.toString()} />
        </div>
      </div>

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
      }}>
        <span>
          Min capital {fmtMoney(listing.min_capital, listing.currency, true)}
        </span>
        <span>{listing.subscriber_count} subscribed</span>
      </div>
    </article>
  )
}

function Stat({ label, value, suffix, tone }: {
  label: string; value?: string | null; suffix?: string; tone?: 'loss'
}) {
  const missing = value === null || value === undefined || value === ''
  return (
    <div>
      <div style={{
        fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </div>
      <div className="num" style={{
        fontSize: 'var(--fs-lg)', fontWeight: 800,
        // A metric that could not be computed shows as an em dash. Rendering a
        // missing Sharpe as 0.00 would be a fabricated number.
        color: missing ? 'var(--text-muted)'
          : tone === 'loss' ? 'var(--loss)' : 'var(--text-primary)',
      }}>
        {missing ? '—' : `${value}${suffix ?? ''}`}
      </div>
    </div>
  )
}

function Tag({ text, fg }: { text: string; fg: string }) {
  return (
    <span style={{
      fontSize: 'var(--fs-2xs)', fontWeight: 800, letterSpacing: '0.06em',
      color: fg, background: 'var(--bg-hover)', border: `1px solid ${fg}33`,
      borderRadius: 'var(--radius-pill)', padding: '2px 8px',
    }}>
      {text}
    </span>
  )
}

// ===========================================================================
function ListingDetail({ listing, canSubscribe, onClose, onSubscribed }: {
  listing: AtlasListing
  canSubscribe: boolean
  onClose: () => void
  onSubscribed: () => void
}) {
  const [accepted, setAccepted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const m = listing.published_metrics

  const subscribe = async () => {
    setBusy(true); setError(null)
    try {
      const r = await api.atlasSubscribe(listing.id)
      setDone(r.note)
      setTimeout(onSubscribed, 1800)
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-label={listing.title}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(16, 24, 40, 0.45)',
        display: 'grid', placeItems: 'center', padding: 'var(--sp-5)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(760px, 100%)', maxHeight: '86vh', overflow: 'auto',
          background: 'var(--bg-panel)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-lg)',
          padding: 'var(--sp-6)',
          display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-4)' }}>
          <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <Tag text={ATLAS_TIERS[listing.tier].short} fg="var(--accent)" />
              <Tag text={listing.risk_grade}
                   fg={RISK_TONE[listing.risk_grade] ?? 'var(--text-muted)'} />
            </div>
            <h2 style={{ fontSize: 'var(--fs-2xl)', fontWeight: 800 }}>
              {listing.title}
            </h2>
            <p style={{
              fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
              marginTop: 6, lineHeight: 1.6,
            }}>
              {listing.long_description ?? listing.summary}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-muted)', fontSize: 20, lineHeight: 1,
                    height: 'fit-content',
                  }}>
            ×
          </button>
        </div>

        <Section title="Published record">
          <div style={{
            fontSize: 'var(--fs-xs)', fontWeight: 800, letterSpacing: '0.05em',
            color: m.result_class === 'LIVE' ? 'var(--ok)' : 'var(--warn)',
            textTransform: 'uppercase',
          }}>
            {resultClassLabel(m.result_class)}
          </div>
          <div style={{
            fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 10,
          }}>
            Period {m.period_start} → {m.period_end}
            {m.computed_at && ` · computed ${fmtDateTime(m.computed_at)}`}
            {m.initial_capital &&
              ` · on ${fmtMoney(m.initial_capital, m.currency ?? 'INR', true)}`}
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-6)', flexWrap: 'wrap' }}>
            <Stat label="Total return" value={m.total_return} suffix="%" />
            <Stat label="CAGR" value={m.cagr} suffix="%" />
            <Stat label="Sharpe" value={m.sharpe_ratio} />
            <Stat label="Max drawdown" value={m.max_drawdown} suffix="%" tone="loss" />
            <Stat label="Win rate" value={m.win_rate} suffix="%" />
            <Stat label="Trades" value={m.trades_count?.toString()} />
          </div>
          {/* Methodology and limitations travel WITH the result, exactly as the
              backtest report requires. A number shown without them is a number
              nobody can check. */}
          {m.methodology && (
            <p style={noteStyle}><b>Methodology.</b> {m.methodology}</p>
          )}
          {m.limitations && (
            <p style={noteStyle}><b>Limitations.</b> {m.limitations}</p>
          )}
        </Section>

        <Section title="What you get">
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr',
                       gap: '6px var(--sp-4)', fontSize: 'var(--fs-sm)' }}>
            <Row k="Tier" v={`${ATLAS_TIERS[listing.tier].label} — ${ATLAS_TIERS[listing.tier].blurb}`} />
            <Row k="Strategy" v={`${listing.strategy_code ?? '—'} v${listing.strategy_version ?? '—'} (pinned)`} />
            <Row k="Instruments" v={listing.symbols.join(', ') || '—'} />
            <Row k="Exchanges" v={listing.exchanges.join(', ') || '—'} />
            <Row k="Minimum capital"
                 v={fmtMoney(listing.min_capital, listing.currency, true)} />
            <Row k="Subscribers" v={String(listing.subscriber_count)} />
          </dl>
        </Section>

        <Section title={listing.tier === 'OPEN' ? 'The rules, in full' : 'The rules'}>
          {listing.tier === 'OPEN' && listing.rule_graph ? (
            <pre className="code" style={{
              background: 'var(--bg-subtle)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: 'var(--sp-3)',
              overflowX: 'auto', margin: 0, lineHeight: 1.5,
            }}>
              {JSON.stringify(listing.rule_graph, null, 2)}
            </pre>
          ) : (
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
                        lineHeight: 1.6, margin: 0 }}>
              This is an {ATLAS_TIERS.EDGE.label} listing: the logic is sealed and
              is not published. What is published is the record above, the risk
              grade, the instruments it trades and its behaviour once running.
              If you need to see the rules before committing capital, choose an{' '}
              {ATLAS_TIERS.OPEN.label} listing instead.
            </p>
          )}
        </Section>

        <Section title="Risk disclosure">
          <p style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.65,
                      color: 'var(--text-secondary)', margin: 0,
                      whiteSpace: 'pre-wrap' }}>
            {listing.disclosure}
          </p>
        </Section>

        {done && (
          <div style={{
            background: 'var(--ok-bg)', border: '1px solid var(--ok)',
            borderRadius: 'var(--radius-md)', padding: 'var(--sp-3)',
            color: 'var(--ok)', fontSize: 'var(--fs-sm)', lineHeight: 1.5,
          }}>
            {done}
          </div>
        )}
        {error && (
          <div style={{
            background: 'var(--danger-bg)', border: '1px solid var(--danger)',
            borderRadius: 'var(--radius-md)', padding: 'var(--sp-3)',
            color: 'var(--danger)', fontSize: 'var(--fs-sm)',
          }}>
            {error}
          </div>
        )}

        {!listing.subscribed && !done && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            <label style={{
              display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start',
              fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
              lineHeight: 1.5, cursor: 'pointer',
            }}>
              <input type="checkbox" checked={accepted}
                     onChange={(e) => setAccepted(e.target.checked)}
                     style={{ marginTop: 3 }} />
              I have read the disclosure above and understand that the published
              figures describe a stated past period and are not a forecast.
            </label>
            <button
              onClick={() => void subscribe()}
              disabled={!accepted || busy || !canSubscribe
                        || listing.status !== 'PUBLISHED'}
              style={{
                alignSelf: 'flex-start',
                background: (!accepted || !canSubscribe) ? 'var(--bg-active)' : 'var(--accent)',
                color: (!accepted || !canSubscribe) ? 'var(--text-muted)' : 'var(--text-inverse)',
                border: 'none', borderRadius: 'var(--radius-md)',
                padding: '10px var(--sp-6)', fontSize: 'var(--fs-sm)',
                fontWeight: 800, fontFamily: 'inherit',
                cursor: (!accepted || !canSubscribe) ? 'not-allowed' : 'pointer',
              }}
            >
              {busy ? 'Subscribing…' : 'Subscribe'}
            </button>
            {!canSubscribe && (
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                Your role can browse the marketplace but not subscribe.
              </span>
            )}
            {listing.status !== 'PUBLISHED' && (
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--warn)' }}>
                This listing is {listing.status.toLowerCase()} and is closed to
                new subscriptions.
              </span>
            )}
          </div>
        )}
        {listing.subscribed && (
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ok)', fontWeight: 700 }}>
            You are subscribed. Deploy it from the Forge screen when you are ready.
          </div>
        )}
      </div>
    </div>
  )
}

const noteStyle: React.CSSProperties = {
  fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', lineHeight: 1.6,
  marginTop: 'var(--sp-3)',
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="qx-card-title" style={{ marginBottom: 'var(--sp-2)' }}>
        {title}
      </h3>
      {children}
    </section>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt style={{ color: 'var(--text-muted)', fontWeight: 700,
                   whiteSpace: 'nowrap' }}>{k}</dt>
      <dd style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>{v}</dd>
    </>
  )
}

// ===========================================================================
function MySubscriptions({ subs, onChanged, canSubscribe }: {
  subs: AtlasSubscription[]; onChanged: () => void; canSubscribe: boolean
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const cancel = async (s: AtlasSubscription) => {
    setBusy(s.id)
    try {
      const r = await api.atlasUnsubscribe(s.id, 'cancelled by the trader')
      setNote(r.note)
      onChanged()
    } catch (e) {
      setNote(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Panel title="My subscriptions">
      {note && (
        <div style={{
          marginBottom: 'var(--sp-3)', padding: 'var(--sp-3)',
          background: 'var(--accent-bg)', border: '1px solid var(--accent)',
          borderRadius: 'var(--radius-md)', color: 'var(--accent)',
          fontSize: 'var(--fs-sm)', lineHeight: 1.5,
        }}>
          {note}
        </div>
      )}
      {subs.length === 0 ? (
        <Empty>
          You have not subscribed to anything yet. Subscribing grants access to a
          strategy; it does not start it.
        </Empty>
      ) : (
        <div className="scroll-x">
          <table className="qx">
            <thead>
              <tr>
                <th>Listing</th><th>Tier</th><th>Version</th><th>Subscribed</th>
                <th>Running</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 700 }}>{s.title}</td>
                  <td>{s.tier}</td>
                  <td className="num">v{s.strategy_version}</td>
                  <td>{fmtDateTime(s.subscribed_at)}</td>
                  <td style={{
                    color: s.run_status === 'RUNNING' ? 'var(--ok)' : 'var(--text-muted)',
                    fontWeight: s.run_status === 'RUNNING' ? 700 : 400,
                  }}>
                    {s.run_status === 'RUNNING'
                      ? `RUNNING (${s.last_deployed_mode ?? '—'})`
                      : 'not deployed'}
                  </td>
                  <td style={{
                    color: s.status === 'ACTIVE' ? 'var(--ok)' : 'var(--text-muted)',
                  }}>
                    {s.status}
                  </td>
                  <td className="r">
                    {s.status === 'ACTIVE' && canSubscribe && (
                      <button
                        onClick={() => void cancel(s)}
                        disabled={busy === s.id}
                        style={{
                          background: 'transparent', border: '1px solid var(--danger)',
                          color: 'var(--danger)', borderRadius: 'var(--radius-sm)',
                          padding: '2px var(--sp-2)', cursor: 'pointer',
                          fontSize: 'var(--fs-2xs)', fontWeight: 700,
                          fontFamily: 'inherit',
                        }}
                      >
                        {busy === s.id ? '…' : 'Cancel'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p style={{
        fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', lineHeight: 1.6,
        marginTop: 'var(--sp-3)',
      }}>
        Cancelling a subscription withdraws your access to future versions. It
        does <b>not</b> stop a strategy that is already running — stop that from
        the Forge screen, deliberately, so an unsubscribe click can never flatten
        a live position by accident.
      </p>
    </Panel>
  )
}
